import React, { useState } from "react";
import emailjs from "emailjs-com";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import Particle from "../Particle";
import laptopImg from "../../Assets/home-main.svg";
import "../../Assets/contact.css";
import { SnackbarProvider, useSnackbar } from "notistack";
import {
  AiOutlineMail,
  AiOutlinePhone,
  AiFillLinkedin,
  AiFillGithub,
} from "react-icons/ai";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL_FIELDS = { name: "", email: "", message: "" };
const INITIAL_TOUCHED = { name: false, email: false, message: false };

function getErrors(fields) {
  const errors = {};

  if (!fields.name.trim()) {
    errors.name = "Name is required.";
  } else if (fields.name.trim().length < 2) {
    errors.name = "Name must be at least 2 characters.";
  }

  if (!fields.email.trim()) {
    errors.email = "Email is required.";
  } else if (!EMAIL_REGEX.test(fields.email.trim())) {
    errors.email = "Please enter a valid email address.";
  }

  if (!fields.message.trim()) {
    errors.message = "Message is required.";
  } else if (fields.message.trim().length < 10) {
    errors.message = "Message must be at least 10 characters.";
  }

  return errors;
}

function ContactMe() {
  const { enqueueSnackbar } = useSnackbar();

  const [fields, setFields] = useState(INITIAL_FIELDS);
  const [touched, setTouched] = useState(INITIAL_TOUCHED);
  const [sending, setSending] = useState(false);

  const errors = getErrors(fields);
  const isFormValid = Object.keys(errors).length === 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Mark all fields as touched to reveal any hidden errors
    setTouched({ name: true, email: true, message: true });

    if (!isFormValid) return;

    setSending(true);

    emailjs
      .send(
        "service_umgs7l2",
        "template_3174r7m",
        {
          name: fields.name.trim(),
          email: fields.email.trim(),
          message: fields.message.trim(),
        },
        "GUrJ1NnFAQQXwxoTV"
      )
      .then(() => {
        enqueueSnackbar("Message sent! I'll get back to you soon.", {
          variant: "success",
        });
        setFields(INITIAL_FIELDS);
        setTouched(INITIAL_TOUCHED);
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar("Failed to send message. Please try again.", {
          variant: "error",
        });
      })
      .finally(() => setSending(false));
  };

  const fieldState = (key) => ({
    isInvalid: touched[key] && !!errors[key],
    isValid: touched[key] && !errors[key],
  });

  return (
    <Container fluid className="about-section" style={{ textAlign: "left" }}>
      <Particle />
      <Container>
        <Row className="justify-content-left mb-0">
          <Col md={7} style={{ paddingTop: "30px", paddingBottom: "50px" }}>
            <h1 className="contact-section-title">
              Let&apos;s <strong className="purple">Connect</strong>
            </h1>

            {/* Contact Info Card */}
            <Card className="quote-card-view contact-info-card">
              <Card.Body>
                <blockquote className="blockquote mb-0">
                  <h2 style={{ marginBottom: "1rem" }}>
                    Reach <strong className="purple">Out</strong>
                  </h2>
                  <p className="contact-info-text">
                    I&apos;m <strong>Muniraj M</strong>, a full-stack developer
                    experienced in <strong>Laravel, PHP, React.js</strong> and
                    more. Feel free to reach out for collaborations, freelance
                    work, or just a chat!
                  </p>

                  <div className="contact-info-links">
                    <p className="contact-info-text">
                      <AiOutlineMail className="purple" />{" "}
                      <a href="mailto:muniraj0925@gmail.com" className="purple">
                        muniraj0925@gmail.com
                      </a>
                    </p>
                    <p className="contact-info-text">
                      <AiOutlinePhone className="purple" />{" "}
                      <a href="tel:+919500631210" className="purple">
                        +91 95006 31210
                      </a>
                    </p>
                    <p className="contact-info-text">
                      <AiFillLinkedin className="purple" />{" "}
                      <a
                        href="https://www.linkedin.com/in/muniraj0925/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="purple"
                      >
                        linkedin.com/in/muniraj0925
                      </a>
                    </p>
                    <p className="contact-info-text">
                      <AiFillGithub className="purple" />{" "}
                      <a
                        href="https://github.com/Muniraj132"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="purple"
                      >
                        github.com/Muniraj132
                      </a>
                    </p>
                  </div>

                  <p className="about-quote">
                    &ldquo;Let&apos;s create something amazing together!&rdquo;
                  </p>
                  <footer className="blockquote-footer">Muniraj M</footer>
                </blockquote>
              </Card.Body>
            </Card>

            {/* Contact Form */}
            <Card className="mt-4 contact-form-card">
              <h3>Send me a message</h3>
              <Form onSubmit={handleSubmit} noValidate>
                <Form.Group className="mb-3" controlId="formName">
                  <Form.Label className="visually-hidden">Your Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={fields.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="name"
                    {...fieldState("name")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label className="visually-hidden">Your Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={fields.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="email"
                    {...fieldState("email")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formMessage">
                  <Form.Label className="visually-hidden">Your Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="message"
                    placeholder="Your Message (min. 10 characters)"
                    value={fields.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    {...fieldState("message")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  disabled={sending}
                >
                  {sending ? "Sending…" : "Send Message"}
                </Button>
              </Form>
            </Card>
          </Col>

          <Col
            md={5}
            style={{ paddingTop: "50px", paddingBottom: "50px" }}
            className="about-img"
          >
            <img
              src={laptopImg}
              alt="Contact illustration"
              className="img-fluid"
            />
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

export default function ContactWrapper() {
  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <ContactMe />
    </SnackbarProvider>
  );
}
